import React from 'react';
import { articles } from '../../mock';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';

const Article1 = () => {
  const article = articles.find(a => a.id === 1);

  const dockerComposeDVWA = `
version: '3.8'
services:
  dvwa:
    image: vulnerables/web-dvwa
    ports:
      - "8080:80"
    restart: always
    environment:
      - MYSQL_USER=dvwa
      - MYSQL_PASSWORD=dvwa
      - MYSQL_DB=dvwa
      - MYSQL_HOST=db
  db:
    image: mysql:5.7
    restart: always
    environment:
      - MYSQL_ROOT_PASSWORD=root
      - MYSQL_USER=dvwa
      - MYSQL_PASSWORD=dvwa
      - MYSQL_DATABASE=dvwa
`;

  const dockerComposeELK = `
version: '3.8'
services:
  elasticsearch:
    image: docker.elastic.co/elasticsearch/elasticsearch:7.17.5
    container_name: elasticsearch
    environment:
      - "discovery.type=single-node"
    ports:
      - "9200:9200"
      - "9300:9300"
    volumes:
      - esdata:/usr/share/elasticsearch/data

  logstash:
    image: docker.elastic.co/logstash/logstash:7.17.5
    container_name: logstash
    ports:
      - "5044:5044"
    volumes:
      - ./logstash.conf:/usr/share/logstash/pipeline/logstash.conf
    depends_on:
      - elasticsearch

  kibana:
    image: docker.elastic.co/kibana/kibana:7.17.5
    container_name: kibana
    ports:
      - "5601:5601"
    depends_on:
      - elasticsearch

volumes:
  esdata:
    driver: local
`;
  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">
          <span className="text-amber">[</span>
          {article.title}
          <span className="text-amber">]</span>
        </h1>
        <div className="text-muted text-center text-sm">
          <span>{article.date}</span> | <span>{article.readTime}</span>
        </div>
        <div className="ascii-line">
          {'━'.repeat(60)}
        </div>
      </div>

      <div className="article-content">
        <p className="mb-4">
          Welcome to this deep dive into building a Security Operations Center (SOC) lab using Docker. In today's cybersecurity landscape, practical experience is paramount. This guide will walk you through creating a cost-effective, reusable, and isolated lab environment to simulate a real-world SOC. We'll be using Docker to orchestrate a set of powerful open-source tools: DVWA (Damn Vulnerable Web Application), ModSecurity (Web Application Firewall), Suricata (Intrusion Detection System), and the ELK Stack (Elasticsearch, Logstash, Kibana) for log management and analysis.
        </p>

        <h2 className="text-2xl font-bold my-4">What is a SOC?</h2>
        <p className="mb-4">
          A SOC is a centralized unit that deals with security issues on an organizational and technical level. It's a team of people, processes, and technologies that continuously monitors and improves an organization's security posture while preventing, detecting, analyzing, and responding to cybersecurity incidents.
        </p>

        <h2 className="text-2xl font-bold my-4">Why Docker?</h2>
        <p className="mb-4">
          Docker provides a lightweight, portable, and isolated environment for running applications. This is ideal for a SOC lab for several reasons:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li><strong>Isolation:</strong> Each tool runs in its own container, preventing conflicts and ensuring a clean setup.</li>
          <li><strong>Reproducibility:</strong> Dockerfiles and Docker Compose allow you to define your entire lab as code, making it easy to rebuild and share.</li>
          <li><strong>Efficiency:</strong> Containers use fewer resources than virtual machines, allowing you to run a complex lab on a single machine.</li>
          <li><strong>Scalability:</strong> You can easily add or remove components as your lab evolves.</li>
        </ul>

        <h2 className="text-2xl font-bold my-4">Setting Up the Lab: A Step-by-Step Guide</h2>
        <p className="mb-4">
          Let's get our hands dirty. We'll start by setting up the vulnerable application, then the security tools, and finally the logging and analysis platform.
        </p>

        <h3 className="text-xl font-bold my-2">1. The Target: Damn Vulnerable Web Application (DVWA)</h3>
        <p className="mb-4">
          First, we need something to attack and defend. DVWA is a PHP/MySQL web application that is damn vulnerable. Its main goals are to be an aid for security professionals to test their skills and tools in a legal environment, help web developers better understand the processes of securing web applications and aid teachers/students to teach/learn web application security in a class room environment.
        </p>
        <p className="mb-4">
          Here's a simple <code>docker-compose.yml</code> to get DVWA up and running:
        </p>
        <SyntaxHighlighter language="yaml" style={docco} className="rounded-lg">
          {dockerComposeDVWA}
        </SyntaxHighlighter>

        <h3 className="text-xl font-bold my-2">2. The Shield: ModSecurity WAF</h3>
        <p className="mb-4">
          ModSecurity is an open-source, cross-platform web application firewall (WAF) engine for Apache, Nginx, and IIS. It has a robust event-based programming language which provides protection from a range of attacks against web applications and allows for HTTP traffic monitoring, logging and real-time analysis. We can deploy it as a reverse proxy in front of our DVWA container.
        </p>

        <h3 className="text-xl font-bold my-2">3. The Watchtower: Suricata IDS</h3>
        <p className="mb-4">
          Suricata is a high performance Network IDS, IPS and Network Security Monitoring engine. It is open source and owned by a community-run non-profit foundation, the Open Information Security Foundation (OISF). Suricata is developed by the OISF and its supporting vendors. We will configure Suricata to monitor the network traffic going to our DVWA.
        </p>

        <h3 className="text-xl font-bold my-2">4. The Brain: ELK Stack (Elasticsearch, Logstash, Kibana)</h3>
        <p className="mb-4">
          The ELK Stack is a collection of three open-source products — Elasticsearch, Logstash, and Kibana. They are all developed, managed ,and maintained by the company Elastic.
        </p>
        <ul className="list-disc list-inside mb-4">
          <li><strong>Elasticsearch</strong> is a distributed, free and open search and analytics engine for all types of data, including textual, numerical, geospatial, structured, and unstructured.</li>
          <li><strong>Logstash</strong> is a free and open server-side data processing pipeline that ingests data from a multitude of sources, transforms it, and then sends it to your favorite "stash" (like Elasticsearch).</li>
          <li><strong>Kibana</strong> is a free and open user interface that lets you visualize your Elasticsearch data and navigate the Elastic Stack.</li>
        </ul>
        <p className="mb-4">
          Here's a <code>docker-compose.yml</code> for a basic ELK setup:
        </p>
        <SyntaxHighlighter language="yaml" style={docco} className="rounded-lg">
          {dockerComposeELK}
        </SyntaxHighlighter>
        
        <h2 className="text-2xl font-bold my-4">Conclusion</h2>
        <p className="mb-4">
          This guide provides the foundational steps to build your own SOC lab with Docker. From here, you can expand the lab by adding more tools, configuring more complex network topologies, and generating more sophisticated attack scenarios. The possibilities are endless. Happy hacking!
        </p>
      </div>
    </div>
  );
};

export default Article1;
